import styled from 'styled-components';
import {TicketDataTableTotalsRow} from './TicketDataTableTotalsRow';
import {TicketDataTable} from './TicketDataTable';
import {DashboardGroupedTickets} from './DashboardGroupedTickets';
import type {DashboardColumnKey} from '../constants/dashboardColumns';
import type {DashboardTicket, DashboardTotals} from '../types/dashboardTicket';
import type {DashboardGroupByToken, TicketGroup} from '../utils/groupTickets';

const Section = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${p => p.theme.sys.spacing[300]};
`;

interface Props {
	tickets: readonly DashboardTicket[];
	/**
	 * The rollups above the table. Passed in rather than derived here so the route
	 * can swap a client-side rollup over the loaded rows for the server-side one over
	 * the whole filtered set (FOR-70) without touching this component.
	 */
	totals: DashboardTotals;
	groupBy: DashboardGroupByToken;
	visibleColumns: ReadonlySet<DashboardColumnKey>;
}

/**
 * The dashboard's ticket table with its totals row (PRD §6.5 / §8.6), laid out by
 * the active grouping (§6.7).
 *
 * The totals row sits above the grouping rather than inside it: it rolls up every
 * ticket in scope, so under a grouping it belongs above all the buckets, and each
 * bucket's own subtotals ride on its header. It renders even with nothing in scope —
 * a "Total 0" is an answer, where an absent row leaves the reader unsure whether the
 * query ran.
 */
export function DashboardTicketSection({tickets, totals, groupBy, visibleColumns}: Props) {
	return (
		<Section data-testid="dashboard-ticket-section">
			<TicketDataTableTotalsRow totals={totals} />
			<DashboardGroupedTickets
				tickets={tickets}
				groupBy={groupBy}
				renderGroup={(groupTickets: DashboardTicket[], group: TicketGroup<DashboardTicket>) => (
					<TicketDataTable
						tickets={groupTickets}
						visibleColumns={visibleColumns}
						testId={groupBy === 'none' ? 'ticket-data-table' : `ticket-data-table-${group.domKey}`}
					/>
				)}
			/>
		</Section>
	);
}
